import { makeObservable, observable, runInAction, action } from 'mobx';
import { ApplicationSdk } from '../../../core/sdk';
import BaseAdminStore, { AdminApiResponse } from '../../../shared/stores/baseAdminStore';
import { MatenRequestResponse } from '../../../shared';

export interface ApiToken {
    id: number;
    name: string;
    active: boolean;
    expires: Date | null;
    lastUsed: Date | null;
    created: Date;
    isExpired: boolean;
}

/**
 * The System Settings store manages the system configuration settings for the admin panel.
 */
class SystemSettingStore extends BaseAdminStore {
    public employeeLicenses: number = 0;
    public employeeCount: number = 0;
    public apiTokens: ApiToken[] = [];
    public apiTokenPreview: any = null;
    public s3Config: {
        accessKeyId: string | null;
        secretAccessKey: string | null;
        bucketName: string | null;
        folderName: string | null;
        endpoint: string | null;
        region: string | null;
    } = {
        accessKeyId: null,
        secretAccessKey: null,
        bucketName: null,
        folderName: null,
        endpoint: null,
        region: null,
    };
    public uchatConfig: {
        uchatApiKey: string | null;
        uchatPartnerApiKey: string | null;
        uchatBotPhoneNumber: string | null;
        uchatWorkspaceId: string | null;
    } = {
        uchatApiKey: null,
        uchatPartnerApiKey: null,
        uchatBotPhoneNumber: null,
        uchatWorkspaceId: null,
    };

    constructor(sdk: ApplicationSdk) {
        super(sdk);

        makeObservable(this, {
            employeeCount: observable,
            employeeLicenses: observable,
            apiTokens: observable,
            apiTokenPreview: observable,
            s3Config: observable,
            uchatConfig: observable,
            deleteTokenPreview: action,
        });
    }

    // Implement required abstract methods
    protected getApiEndpoint(): string {
        return 'admin';
    }

    protected getApiPath(): string {
        return 'system/get';
    }

    protected buildApiParams(): any {
        return {};
    }

    protected processApiResponse(response: AdminApiResponse): any {
        runInAction(() => {
            if (response.status === 200) {
                const { employeeCount, employeeLicenses, apiTokens, s3Config, uchatConfig } =
                    response.json || {};

                this.employeeCount = employeeCount || 0;
                this.employeeLicenses = employeeLicenses || 0;
                this.apiTokens = apiTokens || [];
                this.s3Config = s3Config || {
                    accessKeyId: null,
                    secretAccessKey: null,
                    bucketName: null,
                    folderName: null,
                    endpoint: null,
                    region: null,
                };
                this.uchatConfig = uchatConfig || {
                    uchatApiKey: null,
                    uchatPartnerApiKey: null,
                    uchatBotPhoneNumber: null,
                    uchatWorkspaceId: null,
                };
            }
        });
        return response.json;
    }

    public async saveSettings(
        configKey: string,
        configValue: string | number | Record<string, any>
    ): Promise<MatenRequestResponse> {
        try {
            const response = await this.makeApiCall('admin/system', 'update', {
                configKey,
                configValue,
            });

            if (response.status === 200) {
                return { success: true, status: 200, data: response.json };
            } else {
                return { success: false, status: response.status, error: response.json.error };
            }
        } catch (error) {
            return { success: false, status: 500 };
        }
    }

    public async createApiToken(name: string, expires?: number): Promise<MatenRequestResponse> {
        try {
            const response = await this.makeApiCall('admin/system', 'update', {
                configKey: 'apitoken',
                configValue: { name, expires },
            });

            if (response.status === 200) {
                runInAction(() => {
                    this.apiTokenPreview = response.json.data; // Save the preview token data
                    this.apiTokens.push(this.apiTokenPreview.tokenData); // Add to the local token list
                });
                return { success: true, status: 200, data: response.json };
            } else {
                return { success: false, status: response.status, error: response.json.error };
            }
        } catch (error) {
            return { success: false, status: 500 };
        }
    }

    public deleteTokenPreview(): void {
        this.apiTokenPreview = null;
    }

    public async revokeApiToken(tokenId: number): Promise<MatenRequestResponse> {
        try {
            const response = await this.makeApiCall('admin/system', 'update', {
                configKey: 'apitokenrevoke',
                configValue: tokenId,
            });

            if (response.status === 200) {
                runInAction(() => {
                    this.apiTokens = this.apiTokens.map(token =>
                        token.id === tokenId ? { ...token, active: false } : token
                    );
                });
                return { success: true, status: 200, data: response.json };
            } else {
                const errorCode = response.json?.error || '';

                if (errorCode) {
                    switch (errorCode) {
                        case 'ERR_TOKEN_REVOKE_FAILED':
                            throw new Error('Api token konnte nicht gesperrt werden.');
                        default:
                            break;
                    }
                }
            }
        } catch (error: any) {
            return { success: false, status: 400, error: error.message || 'Unknown error' };
        }

        return { success: false, status: 400, error: 'Unknown error' };
    }

    public async init(): Promise<void> {
        await super.init();
    }

    public async editLicenses(): Promise<void> {
        const licenseChangeResponse = await this.sdk.getDialogNumberInput({
            title: 'Mitarbeiterlizenzen bearbeiten',
            message: 'Hier können Sie die Anzahl der Mitarbeiterlizenzen bearbeiten.',
            initialValue: this.employeeLicenses,
            label: 'Anzahl Mitarbeiterlizenzen',
            onConfirm: async (values: object) => {
                const { input } = values as { input: number };
                return this.saveSettings('employeeLicenses', input);
            },
        });

        if (licenseChangeResponse.confirmed) {
            runInAction(() => {
                const { employeeLicenses, employeeCount } =
                    (
                        licenseChangeResponse.response as {
                            data?: { employeeLicenses?: number; employeeCount?: number };
                        }
                    )?.data || {};
                this.employeeCount = employeeCount || 0;
                this.employeeLicenses = employeeLicenses || 0;
            });

            this.sdk.showSuccessToast('Mitarbeiterlizenzen erfolgreich aktualisiert.');
        } else if (licenseChangeResponse.error) {
            switch (licenseChangeResponse.error) {
                case 'ERR_VALIDATE_CONFIGKEY':
                    this.sdk.showErrorToast(
                        'Unbekannter Konfigurationsschlüssel. Bitte wenden Sie sich an den Support.'
                    );
                    break;
                case 'ERR_INVALID_EMPLOYEE_LICENSES':
                    this.sdk.showErrorToast('Ungültige Anzahl an Mitarbeiterlizenzen.');
                    break;
                default:
                    this.sdk.showErrorToast(
                        `Unbekannter Fehler beim Aktualisieren der Mitarbeiterlizenzen`
                    );
                    break;
            }
        }
    }

    public async saveS3Settings({
        accessKeyId,
        secretAccessKey,
        bucketName,
        folderName,
        endpoint,
        region,
    }: {
        accessKeyId: string | null;
        secretAccessKey: string | null;
        bucketName: string | null;
        folderName: string | null;
        endpoint: string | null;
        region: string | null;
    }): Promise<MatenRequestResponse> {
        try {
            const response = await this.makeApiCall('admin/system', 'update', {
                configKey: 's3storage',
                configValue: {
                    accessKeyId,
                    secretAccessKey,
                    bucketName,
                    folderName,
                    endpoint,
                    region,
                },
            });

            if (response.status === 200) {
                return { success: true, status: 200, data: response.json };
            } else {
                const errorCode = response.json?.error || '';

                if (errorCode) {
                    switch (errorCode) {
                        case 'ERR_INVALID_S3_CONFIG':
                            throw new Error('Ungültige S3-Konfiguration.');
                        default:
                            break;
                    }
                }
            }
        } catch (error: any) {
            return { success: false, status: 400, error: error.message || 'Unknown error' };
        }

        return { success: false, status: 400, error: 'Unknown error' };
    }

    public async saveUChatApiKey(apiKey: string): Promise<MatenRequestResponse> {
        try {
            const response = await this.saveSettings('uchatapikey', apiKey);

            if (response.success) {
                runInAction(() => {
                    this.uchatConfig.uchatApiKey = apiKey;
                });
            }

            return response;
        } catch (error: any) {
            return { success: false, status: 500, error: error.message || 'Unknown error' };
        }
    }

    public async saveUChatPartnerApiKey(apiKey: string): Promise<MatenRequestResponse> {
        try {
            const response = await this.saveSettings('uchatpartnerapikey', apiKey);

            if (response.success) {
                runInAction(() => {
                    this.uchatConfig.uchatPartnerApiKey = apiKey;
                });
            }

            return response;
        } catch (error: any) {
            return { success: false, status: 500, error: error.message || 'Unknown error' };
        }
    }

    public async saveUChatBotPhoneNumber(phoneNumber: string): Promise<MatenRequestResponse> {
        try {
            const response = await this.saveSettings('uchatbotphonenumber', phoneNumber);

            if (response.success) {
                runInAction(() => {
                    this.uchatConfig.uchatBotPhoneNumber = phoneNumber;
                });
            }

            return response;
        } catch (error: any) {
            return { success: false, status: 500, error: error.message || 'Unknown error' };
        }
    }

    public async saveUChatWorkspaceId(workspaceId: string): Promise<MatenRequestResponse> {
        try {
            const response = await this.saveSettings('uchatworkspaceid', workspaceId);

            if (response.success) {
                runInAction(() => {
                    this.uchatConfig.uchatWorkspaceId = workspaceId;
                });
            }

            return response;
        } catch (error: any) {
            return { success: false, status: 500, error: error.message || 'Unknown error' };
        }
    }

    public get hasUChatApiKey(): boolean {
        return !!(this.uchatConfig.uchatApiKey && this.uchatConfig.uchatApiKey.trim().length > 0);
    }

    public get hasUChatPartnerApiKey(): boolean {
        return !!(
            this.uchatConfig.uchatPartnerApiKey &&
            this.uchatConfig.uchatPartnerApiKey.trim().length > 0
        );
    }

    public get hasUChatWorkspaceId(): boolean {
        return !!(
            this.uchatConfig.uchatWorkspaceId && this.uchatConfig.uchatWorkspaceId.trim().length > 0
        );
    }
}

export default SystemSettingStore;
